import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, Home, FileText, ArrowRight, Download } from "lucide-react";
import useSeo from "@/lib/useSeo";
import { api, track } from "@/lib/api";

const AUDIENCE_META = {
  Investor: { icon: Calculator, label: "Property Investors", chip: "bg-brass/15 text-brass border-brass/30" },
  Vendor: { icon: Home, label: "Property Owners", chip: "bg-emerald-500/15 text-emerald-200 border-emerald-400/30" },
  Both: { icon: FileText, label: "Investors & Owners", chip: "bg-white/10 text-emerald-100/80 border-white/20" },
};

export default function Resources() {
  useSeo({ title: "Free Property Resources | Emerald Crown Property Investments", description: "Free tools and guides from Emerald Crown Property Investments for property investors and homeowners.", path: "/resources" });
  const [resources, setResources] = useState([]);
  useEffect(() => {
    track("page_view", { page: "resources" });
    api.get("/resources").then((r) => setResources(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="resources-page">
      <header className="bg-emerald-950 text-white pt-32 pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-brass">Free Downloads</span>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-serif font-semibold">Free Property Resources</h1>
          <p className="mt-5 text-lg text-emerald-100/75 max-w-2xl">Practical tools and guides from Emerald Crown Property Investments for property investors and homeowners.</p>
        </div>
      </header>

      <div className="bg-[#071912]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {resources.map((r) => {
              const meta = AUDIENCE_META[r.audience] || AUDIENCE_META.Both;
              const Icon = meta.icon;
              return (
                <Link key={r.slug} to={`/resources/${r.slug}`} data-testid={`resource-card-${r.slug}`}
                  className="group flex flex-col bg-[#0F2E23] border border-white/10 rounded-2xl p-7 hover:border-brass/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <span className="grid place-items-center w-12 h-12 rounded-xl bg-brass/15"><Icon className="w-6 h-6 text-brass" /></span>
                    <span className={`text-[11px] font-medium px-2.5 py-1 rounded-full border ${meta.chip}`}>{meta.label}</span>
                  </div>
                  <h2 className="mt-5 text-xl font-serif font-semibold text-white">{r.title}</h2>
                  <p className="mt-3 text-sm text-emerald-100/70 leading-relaxed flex-1">{r.description}</p>
                  <span className="mt-6 inline-flex items-center gap-2 bg-brass group-hover:bg-brass-600 text-emerald-950 font-semibold rounded-full px-5 py-2.5 text-sm w-fit transition-colors">
                    <Download className="w-4 h-4" /> {r.cta} <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </Link>
              );
            })}
          </div>
          {resources.length === 0 && <p className="text-emerald-100/50 text-center py-10">Resources are coming soon.</p>}
          <p className="mt-10 text-xs text-emerald-100/50 text-center">More guides and tools are on the way, including refurbishment checklists, a BRR calculator and auction guidance.</p>
        </div>
      </div>
    </div>
  );
}
